import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  partnerGroupChannelQueryKeys,
  usePartnerGroupChannelJoinRequestsQuery,
  usePartnerGroupChannelMessagesQuery,
  usePartnerGroupChannelQuery,
} from '../../../api/use-partner-group-channel-query';
import { useUpdatePartnerGroupChannelMutation } from '../../../api/use-update-partner-group-channel-mutation';
import { useDeclinePartnerGroupChannelJoinRequestMutation } from '../../../api/use-decline-partner-group-channel-join-request-mutation';
import { useApprovePartnerGroupChannelJoinRequestMutation } from '../../../api/use-approve-partner-group-channel-join-request-mutation';
import { useArchivePartnerGroupChannelMutation } from '../../../api/use-archive-partner-group-channel-mutation';
import { useRestorePartnerGroupChannelMutation } from '../../../api/use-restore-partner-group-channel-mutation';
import { useSendPartnerGroupChatMessageMutation } from '../../../api/use-send-partner-group-chat-message-mutation';
import { useUpdatePartnerGroupChatMessageMutation } from '../../../api/use-update-partner-group-chat-message-mutation';
import { useRemovePartnerGroupChannelMemberMutation } from '../../../api/use-remove-partner-group-channel-member-mutation';
import {
  buildChatMessagesFromPages,
  buildPartnerGroupChannelFromPages,
} from './mapPartnerGroupChannel';
import { isPartnerCommunityArchived } from './partnerCommunityArchive';
import { setPartnerCommunityLiveView } from './partnerCommunityLiveView';
import { mapCommunityMemberProfile } from './mapCommunityMemberProfile';
import { getPartnerCardSx } from '../partnerDashboardTheme';
import { PortalPageHeader } from '../../layout/PortalPageHeader';
import { createCommunityEditInitialState } from './communityEditConstants';
import { PARTNER_COMMUNITY_JOIN_REQUESTS_TAB } from '../../../constants/communityNotificationTypes';
import ChatPanel from './widgets/ChatPanel';
import CommunityDetailsPanel from './widgets/CommunityDetailsPanel';
import CommunityEmptyState from './widgets/CommunityEmptyState';
import EditCommunityPanel from './widgets/EditCommunityPanel';
import CommunityJoinRequestsPanel from './widgets/CommunityJoinRequestsPanel';
import CommunityManagementPanel, { COMMUNITY_VIEWS } from './widgets/CommunityManagementPanel';
import CommunityMemberProfileDialog from './widgets/CommunityMemberProfileDialog';
import ArchiveCommunityDialog from './widgets/ArchiveCommunityDialog';
import RestoreCommunityDialog from './widgets/RestoreCommunityDialog';
import ArchivedCommunityNotice from './widgets/ArchivedCommunityNotice';
import ApproveMembershipRequestDialog from './widgets/ApproveMembershipRequestDialog';
import RejectMembershipRequestDialog from './widgets/RejectMembershipRequestDialog';
import BlockCommunityMemberDialog from './widgets/BlockCommunityMemberDialog';
import { usePortalTokens } from '../../../styles/usePortalTokens';
import { useAuth } from '../../../contexts/AuthContext';
import { isPartnerCommunityCreated } from './partnerCommunityAccess';

const resolveCommunityViewFromTab = (tab) => {
  if (tab === PARTNER_COMMUNITY_JOIN_REQUESTS_TAB) {
    return COMMUNITY_VIEWS.JOIN_REQUESTS;
  }
  return COMMUNITY_VIEWS.OVERVIEW;
};

const PartnerCommunity = () => {
  const portal = usePortalTokens();
  const { partnerProfile } = useAuth();
  const communityCreated = isPartnerCommunityCreated(partnerProfile);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeView, setActiveView] = useState(() =>
    resolveCommunityViewFromTab(searchParams.get('tab')),
  );

  const isOverviewView = activeView === COMMUNITY_VIEWS.OVERVIEW;
  const isJoinRequestsView = activeView === COMMUNITY_VIEWS.JOIN_REQUESTS;
  const isEditView = activeView === COMMUNITY_VIEWS.EDIT;
  const shouldLoadChannel =
    communityCreated && (isOverviewView || isEditView || isJoinRequestsView);
  const shouldLoadMessages = communityCreated && isOverviewView;
  const [communitySettings, setCommunitySettings] = useState(() =>
    createCommunityEditInitialState(null),
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    hasNextPage: hasMoreMembers,
    fetchNextPage: fetchNextMembersPage,
    isFetchingNextPage: isFetchingMoreMembers,
  } = usePartnerGroupChannelQuery({
    enabled: shouldLoadChannel,
  });
  const channelPageStatus = data?.pages?.[0]?.status;
  const channel = useMemo(() => buildPartnerGroupChannelFromPages(data?.pages), [data?.pages]);
  const members = channel?.members ?? [];
  const isCommunityArchived = isPartnerCommunityArchived(channel);
  const requireJoinApproval = Boolean(
    channel?.requireJoinApproval ?? communitySettings.requireJoinApproval,
  );
  const shouldLoadJoinRequests = communityCreated && isJoinRequestsView && requireJoinApproval;
  const {
    data: joinRequestsData,
    isLoading: isJoinRequestsLoading,
    isError: isJoinRequestsError,
    error: joinRequestsError,
    refetch: refetchJoinRequests,
  } = usePartnerGroupChannelJoinRequestsQuery({
    enabled: shouldLoadJoinRequests,
  });
  const joinRequests = joinRequestsData?.requests ?? [];
  const {
    data: messagesPages,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchMessages,
  } = usePartnerGroupChannelMessagesQuery({
    enabled: shouldLoadMessages && Boolean(channel?.streamChannelId),
    limit: 20,
    channelId: channel?.streamChannelId ?? null,
  });

  // const [isPrivate, setIsPrivate] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const updateGroupChannelMutation = useUpdatePartnerGroupChannelMutation();
  const approveJoinRequestMutation = useApprovePartnerGroupChannelJoinRequestMutation();
  const declineJoinRequestMutation = useDeclinePartnerGroupChannelJoinRequestMutation();
  const archiveGroupChannelMutation = useArchivePartnerGroupChannelMutation();
  const restoreGroupChannelMutation = useRestorePartnerGroupChannelMutation();
  const sendChatMessageMutation = useSendPartnerGroupChatMessageMutation();
  const updateChatMessageMutation = useUpdatePartnerGroupChatMessageMutation();
  const isSavingCommunity = updateGroupChannelMutation.isPending;
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const isArchivingCommunity = archiveGroupChannelMutation.isPending;
  const isRestoringCommunity = restoreGroupChannelMutation.isPending;
  const [pendingApproveRequest, setPendingApproveRequest] = useState(null);
  const [pendingRejectRequest, setPendingRejectRequest] = useState(null);
  const isApprovingRequest = approveJoinRequestMutation.isPending;
  const isRejectingRequest = declineJoinRequestMutation.isPending;
  const [pendingJoinRequestsCount, setPendingJoinRequestsCount] = useState(0);
  const [pendingBlockMember, setPendingBlockMember] = useState(null);
  const removeGroupChannelMemberMutation = useRemovePartnerGroupChannelMemberMutation();
  const isBlockingMember = removeGroupChannelMemberMutation.isPending;
  const previousCommunityViewRef = useRef(activeView);

  useEffect(() => {
    if (!channel || isEditView || isCommunityArchived) {
      previousCommunityViewRef.current = activeView;
      return;
    }

    const previousView = previousCommunityViewRef.current;
    if (previousView === activeView) {
      return;
    }

    previousCommunityViewRef.current = activeView;

    if (activeView === COMMUNITY_VIEWS.OVERVIEW) {
      void refetch();
      void refetchMessages();
      return;
    }

    if (activeView === COMMUNITY_VIEWS.JOIN_REQUESTS && requireJoinApproval) {
      void refetchJoinRequests();
    }
  }, [
    activeView,
    channel,
    isCommunityArchived,
    isEditView,
    refetch,
    refetchJoinRequests,
    refetchMessages,
    requireJoinApproval,
  ]);

  useEffect(() => {
    if (isCommunityArchived) {
      setActiveView(COMMUNITY_VIEWS.OVERVIEW);
      if (searchParams.get('tab')) {
        setSearchParams({}, { replace: true });
      }
      return;
    }

    const tab = searchParams.get('tab');

    // Edit is local-only; strip legacy ?tab=edit so refresh lands on View Community.
    if (tab === COMMUNITY_VIEWS.EDIT) {
      setActiveView(COMMUNITY_VIEWS.OVERVIEW);
      setSearchParams({}, { replace: true });
      return;
    }

    if (tab === PARTNER_COMMUNITY_JOIN_REQUESTS_TAB && !requireJoinApproval) {
      setActiveView(COMMUNITY_VIEWS.OVERVIEW);
      setSearchParams({}, { replace: true });
      return;
    }

    setActiveView(resolveCommunityViewFromTab(tab));
  }, [isCommunityArchived, requireJoinApproval, searchParams, setSearchParams]);

  useEffect(() => {
    if (requireJoinApproval || activeView !== COMMUNITY_VIEWS.JOIN_REQUESTS) return;
    setActiveView(COMMUNITY_VIEWS.OVERVIEW);
    if (searchParams.get('tab') === PARTNER_COMMUNITY_JOIN_REQUESTS_TAB) {
      setSearchParams({}, { replace: true });
    }
  }, [requireJoinApproval, activeView, searchParams, setSearchParams]);

  useEffect(() => {
    if (!channel || isEditView) return;
    setCommunitySettings(createCommunityEditInitialState(channel));
    setPendingJoinRequestsCount(channel.pendingJoinRequests ?? 0);
  }, [channel, isEditView]);

  useEffect(() => {
    const isChatOverviewActive =
      isOverviewView && Boolean(channel) && !isCommunityArchived && !isEditView;

    setPartnerCommunityLiveView({
      active: isChatOverviewActive,
      streamChannelId: channel?.streamChannelId ?? null,
      gatewayChannelId: channel?.id ?? null,
    });

    return () => {
      setPartnerCommunityLiveView({
        active: false,
        streamChannelId: null,
        gatewayChannelId: null,
      });
    };
  }, [channel, isCommunityArchived, isEditView, isOverviewView]);

  useEffect(() => {
    if (!shouldLoadJoinRequests || joinRequestsData?.status !== 'found') return;
    setPendingJoinRequestsCount(joinRequestsData.requests.length);
  }, [shouldLoadJoinRequests, joinRequestsData]);

  const chatMessages = useMemo(
    () => buildChatMessagesFromPages(messagesPages?.pages ?? []),
    [messagesPages?.pages],
  );

  if (!communityCreated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            color: 'text.primary',
            letterSpacing: '-0.01em',
            mb: 2,
          }}
        >
          Community
        </Typography>
        <CommunityEmptyState message="Your community has not been created yet. Once it is set up, this module will unlock automatically." />
      </Box>
    );
  }

  if (shouldLoadChannel && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (shouldLoadChannel && isError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error?.message || 'Failed to load community channel.'}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => refetch()}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  if (shouldLoadChannel && channelPageStatus === 'forbidden') {
    return (
      <Alert severity="warning" sx={{ borderRadius: '8px' }}>
        {data?.pages?.[0]?.message || 'Partner access is required to view the community channel.'}
      </Alert>
    );
  }

  if (shouldLoadChannel && (channelPageStatus === 'not_found' || !channel)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            color: 'text.primary',
            letterSpacing: '-0.01em',
            mb: 2,
          }}
        >
          Community
        </Typography>
        <CommunityEmptyState message={data?.message} />
      </Box>
    );
  }

  const managementPanel = (
    <CommunityManagementPanel
      activeView={activeView}
      isArchived={isCommunityArchived}
      requireJoinApproval={requireJoinApproval}
      pendingJoinRequests={pendingJoinRequestsCount}
      restoring={isRestoringCommunity}
      onEditDetails={() => {
        setActiveView(COMMUNITY_VIEWS.EDIT);
      }}
      onViewCommunity={() => {
        setActiveView(COMMUNITY_VIEWS.OVERVIEW);
        setSearchParams({}, { replace: true });
      }}
      onJoinRequests={() => {
        setActiveView(COMMUNITY_VIEWS.JOIN_REQUESTS);
        setSearchParams({ tab: PARTNER_COMMUNITY_JOIN_REQUESTS_TAB }, { replace: true });
      }}
      onArchiveCommunity={() => setArchiveDialogOpen(true)}
      onRestoreCommunity={() => setRestoreDialogOpen(true)}
    />
  );

  const handleArchiveCommunityClose = () => {
    if (!isArchivingCommunity) {
      setArchiveDialogOpen(false);
    }
  };

  const handleRestoreCommunityClose = () => {
    if (!isRestoringCommunity) {
      setRestoreDialogOpen(false);
    }
  };

  const handleRestoreCommunityConfirm = async () => {
    try {
      await restoreGroupChannelMutation.mutateAsync();
      setRestoreDialogOpen(false);
      setActiveView(COMMUNITY_VIEWS.OVERVIEW);
      setSearchParams({}, { replace: true });
      toast.success('Community restored successfully');
    } catch (restoreError) {
      toast.error(restoreError?.message || 'Failed to restore community');
    }
  };

  const handleArchiveCommunityConfirm = async () => {
    try {
      await archiveGroupChannelMutation.mutateAsync();
      setArchiveDialogOpen(false);
      setActiveView(COMMUNITY_VIEWS.OVERVIEW);
      setSearchParams({}, { replace: true });
      toast.success('Community archived successfully');
    } catch (archiveError) {
      toast.error(archiveError?.message || 'Failed to archive community');
    }
  };

  const handleApproveRequestClose = () => {
    if (!isApprovingRequest) {
      setPendingApproveRequest(null);
    }
  };

  const removeJoinRequestFromCache = (requestId) => {
    queryClient.setQueryData(partnerGroupChannelQueryKeys.joinRequests(), (current) => {
      if (!current || current.status !== 'found') return current;
      return {
        ...current,
        requests: (current.requests ?? []).filter((item) => item.id !== requestId),
      };
    });
  };

  const decrementPendingJoinRequestsCount = () => {
    setPendingJoinRequestsCount((prev) => Math.max(0, prev - 1));
  };

  const handleApproveRequestConfirm = async () => {
    if (!pendingApproveRequest) return;

    try {
      await approveJoinRequestMutation.mutateAsync({
        requestId: pendingApproveRequest.id,
      });
      removeJoinRequestFromCache(pendingApproveRequest.id);
      decrementPendingJoinRequestsCount();
      toast.success(`${pendingApproveRequest.name} membership request accepted`);
      setPendingApproveRequest(null);
    } catch (approveError) {
      toast.error(approveError?.message || 'Failed to approve membership request');
    }
  };

  const handleRejectRequestClose = () => {
    if (!isRejectingRequest) {
      setPendingRejectRequest(null);
    }
  };

  const handleRejectRequestContinue = async (declinePayload) => {
    if (!pendingRejectRequest) return;

    try {
      await declineJoinRequestMutation.mutateAsync({
        requestId: pendingRejectRequest.id,
        ...declinePayload,
      });
      removeJoinRequestFromCache(pendingRejectRequest.id);
      decrementPendingJoinRequestsCount();
      toast.success(`${pendingRejectRequest.name} membership request declined`);
      setPendingRejectRequest(null);
    } catch (rejectError) {
      toast.error(rejectError?.message || 'Failed to reject membership request');
    }
  };

  const handleBlockMemberClose = () => {
    if (!isBlockingMember) {
      setPendingBlockMember(null);
    }
  };

  const handleBlockMemberConfirm = async () => {
    if (!pendingBlockMember?.id) return;

    try {
      await removeGroupChannelMemberMutation.mutateAsync({
        memberId: pendingBlockMember.id,
      });
      toast.success(`${pendingBlockMember.fullName ?? pendingBlockMember.name} blocked`);
      setPendingBlockMember(null);
    } catch (blockError) {
      toast.error(blockError?.message || 'Failed to block community member');
    }
  };

  const joinRequestsMain = isJoinRequestsError ? (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
        {joinRequestsError?.message || 'Failed to load join requests.'}
      </Alert>
      <Button
        variant="outlined"
        onClick={() => refetchJoinRequests()}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        Try again
      </Button>
    </Box>
  ) : (
    <CommunityJoinRequestsPanel
      requests={joinRequests}
      loading={isJoinRequestsLoading}
      onAccept={(request) => setPendingApproveRequest(request)}
      onDecline={(request) => setPendingRejectRequest(request)}
      onViewProfile={(request) => setSelectedMember(mapCommunityMemberProfile(request))}
    />
  );

  const overviewMain = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {isCommunityArchived ? <ArchivedCommunityNotice archivedAt={channel?.deletedAt} /> : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '3fr 4fr' },
          gap: { xs: 2, sm: 2.5, lg: 3 },
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <CommunityDetailsPanel
            description={communitySettings.description || '—'}
            requireJoinApproval={communitySettings.requireJoinApproval}
            attendeeEligibility={communitySettings.attendeeEligibility}
            members={members}
            memberCount={channel?.memberCount}
            hasMoreMembers={Boolean(hasMoreMembers)}
            loadingMoreMembers={isFetchingMoreMembers}
            readOnly={isCommunityArchived}
            onRequireJoinApprovalChange={(value) =>
              setCommunitySettings((prev) => ({ ...prev, requireJoinApproval: value }))
            }
            onViewProfile={(member) => setSelectedMember(mapCommunityMemberProfile(member))}
            onBlockMember={(member) => setPendingBlockMember(member)}
            onLoadMoreMembers={() => {
              if (hasMoreMembers && !isFetchingMoreMembers) {
                void fetchNextMembersPage();
              }
            }}
          />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <ChatPanel
            communityName={communitySettings.title || channel?.name}
            communityLogo={channel?.imageUrl}
            channelId={channel?.streamChannelId ?? null}
            messages={chatMessages}
            isLoading={isMessagesLoading}
            hasMore={Boolean(hasNextPage)}
            loadingMore={isFetchingNextPage}
            readOnly={isCommunityArchived}
            sending={sendChatMessageMutation.isPending}
            editing={updateChatMessageMutation.isPending}
            onLoadMore={() => fetchNextPage()}
            onSendMessage={(messageText, { replyToMessageId } = {}) =>
              sendChatMessageMutation.mutateAsync({
                messageText,
                attachments: [],
                channelId: channel?.streamChannelId,
                replyToMessageId,
              })
            }
            onEditMessage={(messageId, messageText) =>
              updateChatMessageMutation.mutateAsync({
                messageId,
                messageText,
                channelId: channel?.streamChannelId,
              })
            }
            onRefreshMessages={() => refetchMessages()}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <PortalPageHeader title={communitySettings.title || channel?.name || 'Community'} />
      {!isEditView && (isCommunityArchived || isFetching) ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          {isCommunityArchived ? (
            <Chip
              label="Archived"
              size="small"
              sx={{
                height: 26,
                fontWeight: 700,
                fontSize: '0.75rem',
                bgcolor: 'action.hover',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          ) : null}
          {isFetching ? <CircularProgress size={18} sx={{ color: 'primary.main' }} /> : null}
        </Box>
      ) : null}

      <Box
        sx={{
          ...getPartnerCardSx(portal),
          p: { xs: 2, md: 2.5 },
        }}
      >
        {isEditView && !isCommunityArchived ? (
          <EditCommunityPanel
            key={`edit-${channel?.id ?? 'community'}`}
            channel={channel}
            saving={isSavingCommunity}
            onBack={() => {
              setActiveView(COMMUNITY_VIEWS.OVERVIEW);
            }}
            onSave={async (nextSettings) => {
              try {
                await updateGroupChannelMutation.mutateAsync({ form: nextSettings });
                setCommunitySettings(nextSettings);
                setActiveView(COMMUNITY_VIEWS.OVERVIEW);
                toast.success('Community updated successfully');
              } catch (saveError) {
                toast.error(saveError?.message || 'Failed to save community');
              }
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(0, 7fr) minmax(0, 3fr)',
              },
              gap: { xs: 2, sm: 2.5, lg: 3 },
              alignItems: 'start',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {activeView === COMMUNITY_VIEWS.JOIN_REQUESTS &&
              !isCommunityArchived &&
              requireJoinApproval
                ? joinRequestsMain
                : overviewMain}
            </Box>

            <Box sx={{ minWidth: 0 }}>{managementPanel}</Box>
          </Box>
        )}
      </Box>

      <CommunityMemberProfileDialog
        open={Boolean(selectedMember)}
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />

      <ArchiveCommunityDialog
        open={archiveDialogOpen}
        communityName={channel?.name || 'Community'}
        archiving={isArchivingCommunity}
        onClose={handleArchiveCommunityClose}
        onConfirm={handleArchiveCommunityConfirm}
      />

      <RestoreCommunityDialog
        open={restoreDialogOpen}
        communityName={channel?.name || 'Community'}
        restoring={isRestoringCommunity}
        onClose={handleRestoreCommunityClose}
        onConfirm={handleRestoreCommunityConfirm}
      />

      <ApproveMembershipRequestDialog
        open={Boolean(pendingApproveRequest)}
        applicantName={pendingApproveRequest?.name}
        approving={isApprovingRequest}
        onClose={handleApproveRequestClose}
        onConfirm={handleApproveRequestConfirm}
      />

      <RejectMembershipRequestDialog
        open={Boolean(pendingRejectRequest)}
        applicantName={pendingRejectRequest?.name}
        rejecting={isRejectingRequest}
        onClose={handleRejectRequestClose}
        onContinue={handleRejectRequestContinue}
      />

      <BlockCommunityMemberDialog
        open={Boolean(pendingBlockMember)}
        memberName={pendingBlockMember?.fullName ?? pendingBlockMember?.name}
        blocking={isBlockingMember}
        onClose={handleBlockMemberClose}
        onConfirm={handleBlockMemberConfirm}
      />
    </Box>
  );
};

export default PartnerCommunity;
